import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    DateTimeInput,
    required,
    useTranslate,
    RadioButtonGroupInput,
    Toolbar,
    SaveButton,
    useRedirect,
    useNotify,
    useRecordContext,
    FormDataConsumer,
    SelectArrayInput,
    ReferenceArrayInput,
} from 'react-admin';
import { Box, Alert, Typography, Card, CardContent, IconButton, Chip, Button, Dialog, DialogContent, DialogTitle, FormControlLabel, Checkbox } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
    Campaign,
    TouchApp,
    People,
    Schedule,
    ArrowBack,
    Preview,
    Close,
    Notifications,
    Send,
} from '@mui/icons-material';
import { BannerMultiLanguageFields } from './BannerMultiLanguageFields';
import BannerImageUpload from './BannerImageUpload';
import BannerPreview from './BannerPreview';
import GradientThemeField from './GradientThemeField';
import { gradientThemes } from './gradientThemes';
import IconPicker from './IconPicker';
import ActionTypeSelector from '../messages/ActionTypeSelector';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const BANNER_KNOWN_FIELDS = new Set([
    'title', 'subtitle', 'displayType', 'imageUrl',
    'gradientStartColor', 'gradientEndColor', 'gradientAngle',
    'iconName', 'iconColor', 'actionType', 'actionTarget',
    'promotionContent', 'modalButtonText', 'modalButtonActionType',
    'modalButtonActionTarget', 'position', 'isActive',
    'validFrom', 'validUntil', 'targetType',
    'targetMembershipLevels', 'targetUserIds', 'minAppVersion',
    'displayFrequency', 'maxImpressionsPerUser', 'sendPushOnPublish',
]);

const mapBannerBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const bannerFieldLabelKey = (field: string): string =>
    BANNER_KNOWN_FIELDS.has(field)
        ? `resources.banners.fields.${field}`
        : field;

const EditToolbar = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { setValue, watch } = useFormContext();

    // Watch the resend_push_notification field from the form (not part of the database record)
    const resendPushNotification = watch('resend_push_notification' as any) || false;

    // Check if we're editing an existing banner (not creating a new one)
    // Show the resend checkbox for any existing banner
    const isExistingBanner = record?.id !== undefined;

    const handleResendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue('resend_push_notification' as any, e.target.checked);
    };

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 0 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            {isExistingBanner && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={resendPushNotification}
                            onChange={handleResendChange}
                            color="warning"
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Send fontSize="small" />
                            <Typography variant="body2">
                                {translate('resources.banners.actions.resendPushNotification')}
                            </Typography>
                        </Box>
                    }
                />
            )}
            <Box sx={{ flex: 1 }} />
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = ({ record }: { record?: any }) => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/banners')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.banners.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.banners.edit.title')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" color="text.secondary">
                        {record?.title || translate('resources.banners.edit.subtitle')}
                    </Typography>
                    {record?.isActive ? (
                        <Chip label="Active" color="success" size="small" />
                    ) : (
                        <Chip label="Inactive" color="default" size="small" />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const BannerImageUploadWrapper = ({ source }: { source: string }) => {
    const translate = useTranslate();

    return (
        <BannerImageUpload
            source={source}
            label={translate('resources.banners.fields.image')}
            accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] }}
            maxSize={5242880}
        />
    );
};

const MainActionWrapper = () => {
    const { setValue, watch } = useFormContext();
    const translate = useTranslate();
    const actionType = watch('actionType') || 'none';
    const actionTarget = watch('actionTarget');

    // For screen type, use the ActionTypeSelector to get dropdown
    if (actionType === 'screen') {
        return (
            <ActionTypeSelector
                actionType={actionType}
                actionTarget={actionTarget}
                onActionTypeChange={(value) => setValue('actionType', value)}
                onActionTargetChange={(value) => setValue('actionTarget', value)}
            />
        );
    }

    // For URL and deeplink, use text input
    if (actionType === 'url' || actionType === 'deeplink') {
        return (
            <TextInput
                source="actionTarget"
                label={translate('resources.banners.fields.actionTarget')}
                helperText={
                    actionType === 'url'
                        ? translate('resources.banners.helpers.url')
                        : translate('resources.banners.helpers.deeplink')
                }
                fullWidth
            />
        );
    }

    return null;
};

const ModalButtonActionWrapper = () => {
    const { setValue, watch } = useFormContext();
    const modalButtonActionType = watch('modalButtonActionType') || 'none';
    const modalButtonActionTarget = watch('modalButtonActionTarget');

    return (
        <ActionTypeSelector
            actionType={modalButtonActionType}
            actionTarget={modalButtonActionTarget}
            onActionTypeChange={(value) => setValue('modalButtonActionType', value)}
            onActionTargetChange={(value) => setValue('modalButtonActionTarget', value)}
        />
    );
};

const GradientFieldsWrapper = () => {
    const { setValue, watch } = useFormContext();
    const translate = useTranslate();
    const iconName = watch('iconName');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <GradientThemeField
                source="gradientTheme"
                label={translate('resources.banners.fields.gradientTheme')}
                onThemeChange={(theme) => {
                    // Update all gradient fields when theme is selected
                    setValue('gradientStartColor', theme.startColor);
                    setValue('gradientEndColor', theme.endColor);
                    setValue('gradientAngle', theme.angle);
                }}
            />
            <IconPicker
                source="iconName"
                label={translate('resources.banners.fields.iconName')}
                value={iconName}
                onChange={(value) => setValue('iconName', value)}
            />
            {/* Hidden inputs to store gradient values */}
            <Box sx={{ display: 'none' }}>
                <TextInput source="gradientStartColor" />
                <TextInput source="gradientEndColor" />
                <WheelSafeNumberInput source="gradientAngle" />
                <TextInput source="iconName" />
            </Box>
        </Box>
    );
};

const BannerEdit = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    const notify = useNotify();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any>({});
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    return (
        <>
            <Edit
            title={false}
            actions={false}
            component="div"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.banners.notifications.updated', { type: 'success' });
                    redirect('/banners');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapBannerBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(bannerFieldLabelKey(field), { _: field });
                            summaryLines.push(`\u2022 ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                }
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' }, pb: { xs: 10, sm: 2 } }}>
                    <FormDataConsumer>
                        {({ formData }) => (
                            <PageHeader record={formData} />
                        )}
                    </FormDataConsumer>
                    {/* Preview Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <FormDataConsumer>
                            {({ formData }) => (
                                <Button
                                    variant="outlined"
                                    startIcon={<Preview />}
                                    onClick={() => {
                                        setPreviewData(formData);
                                        setPreviewOpen(true);
                                    }}
                                    sx={{
                                        borderColor: '#00C853',
                                        color: '#00C853',
                                        '&:hover': {
                                            borderColor: '#00A843',
                                            backgroundColor: 'rgba(0, 200, 83, 0.08)',
                                        },
                                    }}
                                >
                                    {translate('resources.banners.preview.title')}
                                </Button>
                            )}
                        </FormDataConsumer>
                    </Box>

                    {/* Basic Information */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Campaign sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.banners.sections.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="title"
                                    label={translate('resources.banners.fields.title')}
                                    validate={required()}
                                    fullWidth
                                />

                                <RadioButtonGroupInput
                                    source="displayType"
                                    label={translate('resources.banners.fields.displayType')}
                                    validate={required()}
                                    choices={[
                                        { id: 'image', name: translate('resources.banners.displayTypes.image') },
                                        { id: 'gradient', name: translate('resources.banners.displayTypes.gradient') },
                                    ]}
                                    row
                                />

                                <FormDataConsumer>
                                    {({ formData }) => {
                                        // Only show subtitle for gradient banners
                                        if (formData.displayType === 'gradient') {
                                            return (
                                                <TextInput
                                                    source="subtitle"
                                                    label={translate('resources.banners.fields.subtitle')}
                                                    helperText={translate('resources.banners.helpers.subtitleGradientOnly')}
                                                    fullWidth
                                                />
                                            );
                                        }
                                        return null;
                                    }}
                                </FormDataConsumer>

                                <FormDataConsumer>
                                    {({ formData, ...rest }) => {
                                        return formData.displayType === 'image' ? (
                                            <BannerImageUploadWrapper source="imageUrl" />
                                        ) : (
                                            <GradientFieldsWrapper />
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Action Configuration */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <TouchApp sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.banners.sections.action')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectInput
                                    source="actionType"
                                    label={translate('resources.banners.fields.actionType')}
                                    choices={[
                                        { id: 'none', name: translate('resources.banners.actionTypes.none') },
                                        { id: 'promotion_modal', name: translate('resources.banners.actionTypes.promotionModal') },
                                        { id: 'screen', name: translate('resources.banners.actionTypes.screen') },
                                        { id: 'url', name: translate('resources.banners.actionTypes.url') },
                                        { id: 'deeplink', name: translate('resources.banners.actionTypes.deeplink') },
                                    ]}
                                    fullWidth
                                />

                                <FormDataConsumer>
                                    {({ formData }) => {
                                        if (formData.actionType === 'none') return null;

                                        return (
                                            <>
                                                {/* Action Target - only for screen, url, deeplink */}
                                                {formData.actionType !== 'promotion_modal' && formData.actionType !== 'none' && (
                                                    <MainActionWrapper />
                                                )}
                                                {formData.actionType === 'promotion_modal' && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <TextInput
                                                            source="promotionContent"
                                                            label={translate('resources.banners.fields.promotionContent')}
                                                            multiline
                                                            rows={6}
                                                            fullWidth
                                                        />

                                                        {/* Modal Button Configuration */}
                                                        <Box sx={{
                                                            mt: 2,
                                                            p: { xs: 2, sm: 3 },
                                                            bgcolor: 'grey.50',
                                                            borderRadius: 1
                                                        }}>
                                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#00C853' }}>
                                                                {translate('resources.banners.sections.modalButton')}
                                                            </Typography>

                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <TextInput
                                                                    source="modalButtonText"
                                                                    label={translate('resources.banners.fields.modalButtonText')}
                                                                    helperText={translate('resources.banners.helpers.modalButtonText')}
                                                                    fullWidth
                                                                />

                                                                <Typography variant="subtitle2" sx={{ mt: 1, mb: -1, color: 'text.secondary' }}>
                                                                    {translate('resources.banners.fields.modalButtonAction')}
                                                                </Typography>
                                                                <ModalButtonActionWrapper />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </>
                                        );
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Targeting */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <People sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.banners.sections.targeting')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <RadioButtonGroupInput
                                    source="targetType"
                                    label={translate('resources.banners.fields.targetType')}
                                    choices={[
                                        { id: 'all', name: translate('resources.banners.targetTypes.all') },
                                        { id: 'membership', name: translate('resources.banners.targetTypes.membership') },
                                    ]}
                                    row
                                />

                                <FormDataConsumer>
                                    {({ formData }) => {
                                        if (formData.targetType === 'membership') {
                                            return (
                                                <SelectArrayInput
                                                    source="targetMembershipLevels"
                                                    label={translate('resources.banners.fields.targetMembershipLevels')}
                                                    choices={[
                                                        { id: 'standard', name: translate('resources.banners.memberships.standard') },
                                                        { id: 'silver', name: translate('resources.banners.memberships.silver') },
                                                        { id: 'gold', name: translate('resources.banners.memberships.gold') },
                                                        { id: 'platinum', name: translate('resources.banners.memberships.platinum') },
                                                        { id: 'vip', name: translate('resources.banners.memberships.vip') },
                                                    ]}
                                                    fullWidth
                                                />
                                            );
                                        }
                                        return null;
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Multi-language Translations Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <BannerMultiLanguageFields formData={formData} />
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>

                    {/* Schedule & Display */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Schedule sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.banners.sections.schedule')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <BooleanInput
                                        source="isActive"
                                        label={translate('resources.banners.fields.isActive')}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <DateTimeInput
                                            source="validFrom"
                                            label={translate('resources.banners.fields.validFrom')}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <DateTimeInput
                                            source="validUntil"
                                            label={translate('resources.banners.fields.validUntil')}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <WheelSafeNumberInput
                                            source="position"
                                            label={translate('resources.banners.fields.position')}
                                            min={0}
                                            helperText={translate('resources.banners.helpers.position')}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <SelectInput
                                            source="displayFrequency"
                                            label={translate('resources.banners.fields.displayFrequency')}
                                            choices={[
                                                { id: 'always', name: translate('resources.banners.frequencies.always') },
                                                { id: 'once', name: translate('resources.banners.frequencies.once') },
                                                { id: 'daily', name: translate('resources.banners.frequencies.daily') },
                                                { id: 'weekly', name: translate('resources.banners.frequencies.weekly') },
                                            ]}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>

                                <WheelSafeNumberInput
                                    source="maxImpressionsPerUser"
                                    label={translate('resources.banners.fields.maxImpressions')}
                                    min={0}
                                    helperText={translate('resources.banners.helpers.maxImpressions')}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>

        {/* Preview Modal */}
        <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #E0E0E0',
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {translate('resources.banners.preview.title')}
                </Typography>
                <IconButton
                    onClick={() => setPreviewOpen(false)}
                    size="small"
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('resources.banners.preview.mobilePreview')}
                </Typography>
                <BannerPreview data={previewData} />
            </DialogContent>
        </Dialog>
        </>
    );
};

export default BannerEdit;